import styled, { css } from "styled-components"

import { when, maxWidth, minWidth, eq, transparentize } from "utils"

import HeroContent from "components/hero/content"
import CloudinaryImage from "components/cloudinary-image"

export const Vertical = styled.div`
  h3 {
    font-size: 2.6rem;
    margin-top: 3rem;
  }

  p,
  li {
    line-height: 2.4rem;
    font-size: 1.8rem;
    margin: 1rem 0;
  }

  ${when("centered")`
    text-align: center;
    button {
      margin: 0 auto;
    }
  `}
`

export const SpacerMiddle = styled.div`
  margin-top: 2rem;
`

export const SpacerBottom = styled.div`
  margin-top: 3rem;
`

export const BackgroundContainer = styled.section`
  position: relative;
  background-color: ${({ theme }) => theme.component.background};
  color: ${({ theme }) => theme.component.text};
  display: ${({ hideOnPageLoad }) => (hideOnPageLoad ? "none" : "block")};
  padding-bottom: 2rem;

  ${minWidth.tablet`
    padding: 2.5rem 2rem;
  `}

  ${minWidth.largest`
    ${when("minHeight")`
      min-height: ${({ minHeight }) => minHeight / 10}rem;
      align-self: stretch;
      display: ${({ hideOnPageLoad }) => (hideOnPageLoad ? "none" : "flex")};
      `}
    padding: 4.5rem 0rem;
  `}
`

export const BackgroundImage = styled(CloudinaryImage)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`

export const Horizontal = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;

  > * {
    max-width: 50%;
    flex: 0 0 50%;
  }

  ${maxWidth.desktop`
    flex-direction: column;
    align-items: flex-start;

    > * {
      max-width: 100%;
      flex: 1 0 auto;
    }
  `}

  ${when("applyTopBottomPadding")`
    padding: 6rem 0;
  `}

  ${eq("verticalAlign", "top")`
    align-items: flex-start;
  `}

  ${eq("verticalAlign", "bottom")`
    align-items: flex-end;
  `}
`

export const Left = styled.div`
  padding: 6rem 7.3rem;

  ${maxWidth.tablet`
    padding: 4rem;
  `}

  ${maxWidth.mobile`
    padding: 2rem;
  `}

  ${({ variant }) =>
    variant === "Hero" ||
    (variant === "Hero-Centered" &&
      css`
        padding: 8.5rem 2rem 8.5rem 23.8rem;

        ${maxWidth.desktopL`
          padding: 6.5rem 2rem 6.5rem 16rem;
        `}

        ${maxWidth.desktop`
          padding: 8.5rem 16rem;
        `}

        ${maxWidth.tablet`
          padding: 8.5rem;
        `}

        ${maxWidth.mobile`
          padding: 3.4rem;
        `}

        ${variant === "Hero-Centered" && `text-align: center;`}
      `)}

  ${({ variant }) =>
    variant === "Centered" &&
    `
      text-align: center;

      li {
        text-align: left;
      }
    `}

  h3 {
    font-size: 1.4rem;
    text-transform: uppercase;
    letter-spacing: 0.2rem;
    margin-bottom: 1.2rem;

    &.monoline {
      font-size: 3.2rem;
    }

    ${({ theme }) =>
      !theme.component.isDarkBackground &&
      `
        color: #767676;
      `}

    ${eq("preHeadingStyle", "Large Lower-Case")`
      font-size: 2.7rem;
      line-height: 3rem;
      text-transform: initial;
      letter-spacing: 0;

      ${({ theme }) => !theme.component.isDarkBackground && `color: #767676;`}
    `}
  }

  h1 {
    font-size: 3.6rem;
    line-height: 1.3;
    color: ${({ theme }) => theme.component.largeHeading};

    ${({ variant }) =>
      (variant === "Hero" || variant === "Hero-Centered") &&
      `
        font-size: 4.8rem;
        line-height: 5.6rem;
      `}
  }

  h4 {
    margin-top: 1rem;
    text-align: left !important;
  }
  > h4 {
    font-size: 1.6rem;
    line-height: 2.4rem;
    font-weight: 400;
    margin-top: 1rem;

    ${({ theme }) => !theme.component.isDarkBackground && `color: #767676;`}
  }

  ul {
    margin: 1em 0 1em 2rem;
    padding: 0;
    width: fit-content;

    ${({ variant }) =>
      variant === "Centered" &&
      `
        margin: 1em auto;
      `}
  }

  > div > p,
  li {
    font-size: 2.1rem;
    line-height: 2.8rem;
  }

  button {
    ${({ variant }) => ["Hero-Centered", "Centered"].includes(variant) && `margin: 0 auto;`}
    ${when("isShop")`
      width: 100%;

      ${minWidth.mobile`
        width: unset;
      `}
    `}
  }

  ${maxWidth.hamburgerLarge`
    display: flex;
    flex-direction: column;
    align-items: center;
  `}
`

export const StyledHeroContent = styled(HeroContent)`
  ${when("isShop")`
    h3 {
      margin-bottom: 3rem;
      color: ${({ theme }) => theme.colors.primaryBlue};
    }

    h1 {
      font-size: 3.2rem;
      font-weight: 400;
      line-height: 4.32rem;

      ${maxWidth.mobile`
        font-size: 2.4rem;
        line-height: 3.24rem;
      `}
    }

    p {
      font-size: 1.6rem;
      line-height: 2.496rem;
    }
  `}

  h3 {
    font-size: 1.4rem;
    font-weight: 400;
    line-height: 1.8rem;
    letter-spacing: 0.28px;
    text-transform: capitalize;
    color: #000;
    display: flex;
    align-items: center;
    gap: 1rem;

    span {
      margin-top: 0.15rem;
      img {
        margin-right: -0.1rem;
      }
    }

    ${maxWidth.hamburgerLarge`
      justify-content: center;
    `}
  }

  ${when("isHomePage")`
    h2,
    b,
    strong {
     font-size: 5.8rem;
     font-weight: 700;
     line-height: 121%;
     text-transform: lowercase;
     color: #12233f;

    ${maxWidth.hamburgerLarge`
      text-align: center;
      font-size: 4rem;
    `}
    }

    i,
    em {
     font-style: italic;
     font-family: Abril, "Helvetica Neue", Helvetica, Arial, sans-serif;
     font-weight: 400;
     color: #12233f !important;
    }

    div {
     font-size: 1.9rem;
     font-weight: 400;
     line-height: 2.6rem;
     color: #222;
     width: 70%;

    ${maxWidth.hamburgerLarge`
      width: 100%;
      font-size: 1.9rem;
      text-align: center;
      padding: 0 3.6rem;
    `}
    }
  `}
`

export const Right = styled.div`
  align-self: ${({ variantRight }) => (variantRight ? "auto" : "stretch")};
  position: relative;

  ${when("reverseOrder")`
    order: -1;
  `}

  ${when("applyMediaPadding")`
    padding: 6rem 7.3rem;

    ${maxWidth.tablet`
      padding: 0 4rem 4rem 4rem;
    `}

    ${maxWidth.mobile`
      padding: 0;
    `}
  `}

  ${maxWidth.hamburgerLarge`
    padding: 0 3.6rem;
  `}
`

export const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: ${({ theme }) => {
    const bg = theme.component.background
    return `linear-gradient(90deg, ${transparentize(0.25, bg)} 0%, ${transparentize(
      0.2,
      bg,
    )} 17%, ${transparentize(0.5, bg)} 49%, ${transparentize(0.85, bg)} 59%, ${transparentize(
      1.0,
      bg,
    )} 100%)`
  }};
`

export const ButtonsContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;

  ${eq("variant", "Hero")`
    justify-content: flex-start;
    align-items: flex-start;
  `};

  > :first-child {
    margin-right: 1.5rem;
  }

  > :last-child {
    margin-left: 1.5rem;
  }

  section {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  ${maxWidth.desktop`
    flex-direction: column;
    align-items: center;
    justify-content: center;

    section {
      margin-top: 2rem;
    }

    > :first-child{
      margin-left: 0;
      margin-right: 0;
    }

    > :last-child {
      margin-left: 0;
      margin-right: 0;
    }
  `}

  ${maxWidth.hamburgerLarge`
    justify-content: center;
  `}
`
